/**
 * Integration — one stored credential document per registry id, at
 * `workspaces/{orgId}/integrations/{id}`. `{id}` is an `IntegrationId`
 * (integrations.const.ts); every value in every map is ciphertext under
 * the workspace's own data key (workspace-key.entity.ts), never plaintext
 * and never a `*Cipher`-named sibling field. Declares zero actions — no
 * `read`, no `write` of any kind — so the emitted rules carry no `match`
 * block for this path at all and the default-deny tail (`match
 * /{document=**} { allow read, write: if false; }`) covers it: no client
 * can ever read or write these docs; only the Admin SDK touches them. See
 * workspace.entity.ts's WorkspaceSettings for the member-readable status
 * blocks (verification facts, never a value) the Integrations surface
 * renders instead.
 *
 * One generic shape for every id — which keys `fields` carries is the
 * registry entry's `secrets` declaration, not a per-provider schema here.
 *
 * Fields justified:
 *   fields   — declared-field-name → ciphertext, one entry per field the
 *              id's registry entry declares: the operator-typed
 *              credentials.
 *   issued   — provider-issued secrets → ciphertext: webhook signing
 *              secrets a provider mints when a save registers its webhook
 *              — never operator-typed, so never a declared registry field,
 *              and keyed per registration where the provider issues one
 *              secret per registered endpoint; nullish — most
 *              integrations have no provider-issued secrets.
 *   previous — the `fields` map one generation back, kept so a save that
 *              stores a broken credential can be rolled back; nullish — a
 *              first-generation document has no prior generation.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const IntegrationSchema = z.object({
	fields: z
		.record(z.string().min(1), z.string().min(1))
		.describe(
			"the integration's declared secret fields, each value ciphertext under the workspace key",
		),
	issued: z
		.record(z.string().min(1), z.string().min(1))
		.nullish()
		.describe(
			"the integration's provider-issued secrets — webhook signing secrets captured at registration, never operator-typed — each value ciphertext under the workspace key",
		),
	previous: z
		.record(z.string().min(1), z.string().min(1))
		.nullish()
		.describe(
			"the fields map one generation back, ciphertext, kept for rollback",
		),
});
export type Integration = z.infer<typeof IntegrationSchema>;

export const Integration = entity<Actor>()({
	schema: IntegrationSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/integrations/{id}",
		}),
	);
